import React, { useState } from 'react';
import { getCurrentAssistant, uploadPdfAndCreateAssistant } from './DataServices/AssistantDataService';
import { DocumentTextIcon, CloudArrowUpIcon, CheckCircleIcon, XCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';

const AssistantPdfUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [currentAssistantId, setCurrentAssistantId] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);

  // Fetch current assistant ID on component mount
  React.useEffect(() => {
    fetchCurrentAssistant();
  }, []);

  const fetchCurrentAssistant = async () => {
    try {
      const response = await getCurrentAssistant();
      if (response.assistantId) {
        setCurrentAssistantId(response.assistantId);
      }
    } catch (error) {
      console.error('Error fetching current assistant:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        setMessage('');
      } else {
        setMessage('Please select a PDF file');
        setFile(null);
      }
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
        setMessage('');
      } else {
        setMessage('Please select a PDF file');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage('Please select a file first');
      return;
    }

    setUploading(true);
    setMessage('Uploading PDF and creating new assistant...');

    try {
      const response = await uploadPdfAndCreateAssistant(file);

      if (response.success) {
        setMessage(`✅ Knowledge Base updated with Assistant ID: ${response.assistantId}`);
        setCurrentAssistantId(response.assistantId);
        setFile(null);
        
        // Hide message after 5 seconds
        setTimeout(() => {
          setMessage('');
        }, 5000);
      } else {
        setMessage('❌ Upload failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">AI Assistant Knowledge Base</h1>
          <p className="text-lg text-gray-600">Upload and manage your property management guidelines</p>
        </div>

        

        {/* Main Upload Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Upload Section */}
          <div className="p-8">
            <div
              className={`relative border-2 border-dashed rounded-xl transition-all duration-200 ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400'
              } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                id="pdf-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
              />
              
              <label
                htmlFor="pdf-upload"
                className="flex flex-col items-center justify-center py-12 px-6 cursor-pointer"
              >
                <CloudArrowUpIcon className={`h-16 w-16 mb-4 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="text-lg font-semibold text-gray-700 mb-2">
                  {dragActive ? 'Drop your PDF here' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-500">PDF files only (Max 10MB)</p>
              </label>
            </div>

            {/* Selected File Display */}
            {file && (
              <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="h-8 w-8 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-600">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  {!uploading && (
                    <button
                      onClick={() => setFile(null)}
                      className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <XCircleIcon className="h-6 w-6" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className={`mt-6 w-full flex items-center justify-center gap-2 py-4 px-6 text-base font-semibold rounded-xl transition-all duration-200 ${
                !file || uploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
              }`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="h-5 w-5" />
                  <span>Upload & Create Assistant</span>
                </>
              )}
            </button>

            {/* Status Message */}
            {message && (
              <div
                className={`mt-6 p-4 rounded-xl border flex items-start gap-3 ${
                  message.includes('✅')
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {message.includes('✅') ? (
                  <CheckCircleIcon className="h-6 w-6 flex-shrink-0" />
                ) : (
                  <XCircleIcon className="h-6 w-6 flex-shrink-0" />
                )}
                <p className="text-sm font-medium">{message}</p>
              </div>
            )}
          </div>

          {/* Instructions Section */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-t border-gray-200 p-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-purple-600" />
              How it works
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <p className="text-sm text-gray-700">Upload a PDF containing your property management guidelines and policies</p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <p className="text-sm text-gray-700">The system creates a new AI assistant trained on your document</p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <p className="text-sm text-gray-700">The assistant uses this knowledge to answer tenant inquiries automatically</p>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                <p className="text-sm text-gray-700">Previous assistant will be replaced when you upload a new document</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistantPdfUpload;
